import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import api, { brMonthStr } from "@/lib/api";
import { toast } from "sonner";
import { useSSE } from "@/hooks/useSSE";
import AdminOverview from "./AdminOverview";
import AdminCompanies from "./AdminCompanies";
import AdminAnticipations from "./AdminAnticipations";
import AdminAudit from "./AdminAudit";

const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const TAB_TITLES = {
  overview: "Painel Administrativo",
  companies: "Empresas",
  anticipations: "Todas as Antecipações",
  audit: "Auditoria",
};

export default function AdminDashboard({ tab: initialTab }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab || "overview");
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [anticipations, setAnticipations] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const brMonth = brMonthStr(); // "YYYY-MM" in BRT
  const [selectedYear, setSelectedYear] = useState(Number(brMonth.split("-")[0]));
  const [selectedMonth, setSelectedMonth] = useState(Number(brMonth.split("-")[1]));

  const monthStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;

  useEffect(() => { if (initialTab) setActiveTab(initialTab); }, [initialTab]);

  const [antPage, setAntPage] = useState(1);
  const [antPagination, setAntPagination] = useState(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPagination, setAuditPagination] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, companiesRes, antRes, logsRes] = await Promise.all([
        api.get(`/admin/stats-by-month?month=${monthStr}`),
        api.get("/companies"),
        api.get(`/anticipations?page=${antPage}&page_size=20`),
        api.get(`/admin/audit-logs?page=${auditPage}&page_size=50`).catch(() => ({ data: { items: [], pagination: null } })),
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      const antData = antRes.data;
      setAnticipations(antData.items || antData);
      setAntPagination(antData.pagination || null);
      const logData = logsRes.data;
      setAuditLogs(logData.items || logData);
      setAuditPagination(logData.pagination || null);
    } catch { toast.error("Erro ao carregar dados do painel"); }
    setLoading(false);
  }, [monthStr, antPage, auditPage]);

  useEffect(() => { loadData(); }, [loadData]);

  useSSE(useCallback((event) => {
    if (event === "dashboard_update") loadData();
  }, [loadData]));

  const prevMonth = () => {
    if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(selectedYear - 1); } else setSelectedMonth(selectedMonth - 1);
  };
  const nextMonth = () => {
    if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(selectedYear + 1); } else setSelectedMonth(selectedMonth + 1);
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6 lg:space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight">{TAB_TITLES[activeTab] || "Admin"}</h1>
          <p className="text-sm text-muted-foreground mt-1">Bem-vindo, {user?.name}</p>
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-1.5 self-start" data-testid="admin-month-selector">
          <Button variant="ghost" size="icon" onClick={prevMonth} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid="admin-prev-month">
            <ArrowLeft className="w-3.5 h-3.5" />
          </Button>
          <div className="flex items-center gap-1.5 min-w-[140px] justify-center">
            <Calendar className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm font-semibold">{MONTHS[selectedMonth - 1]} {selectedYear}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={nextMonth} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid="admin-next-month">
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {(activeTab === "overview" || !activeTab) && (
        <AdminOverview user={user} stats={stats} monthStr={monthStr}
          selectedYear={selectedYear} selectedMonth={selectedMonth}
          prevMonth={prevMonth} nextMonth={nextMonth} />
      )}
      {activeTab === "companies" && (
        <AdminCompanies companies={companies} onRefresh={loadData} />
      )}
      {activeTab === "anticipations" && (
        <AdminAnticipations anticipations={anticipations} onRefresh={loadData}
          pagination={antPagination} onPageChange={setAntPage} />
      )}
      {activeTab === "audit" && (
        <AdminAudit auditLogs={auditLogs}
          pagination={auditPagination} onPageChange={setAuditPage} />
      )}
    </div>
  );
}
